import { Link } from 'react-router-dom';
import { PolicyLayout } from '../components/PolicyLayout';
import { ContactBlock } from '../components/ContactBlock';
import { ROUTES } from '../constants';

const SECTIONS = [
  { id: 'introduction', title: 'Introduction' },
  { id: 'who-we-are', title: 'Who We Are' },
  { id: 'information-we-collect', title: 'Information We Collect' },
  { id: 'special-category-data', title: 'Special Category Data (Health Data)' },
  { id: 'how-we-use-information', title: 'How We Use Your Information' },
  { id: 'legal-basis', title: 'Legal Basis for Processing' },
  { id: 'data-sharing', title: 'Data Sharing' },
  { id: 'data-retention', title: 'Data Retention' },
  { id: 'your-rights', title: 'Your Rights Under UK GDPR' },
  { id: 'childrens-privacy', title: "Children's Privacy" },
  { id: 'cookies', title: 'Cookies and Tracking Technologies' },
  { id: 'data-security', title: 'Data Security' },
  { id: 'international-transfers', title: 'International Data Transfers' },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'contact', title: 'Contact Us' },
];

export const PrivacyPolicy = () => {
  return (
    <PolicyLayout title="Privacy Policy" lastUpdated="22 April 2026" sections={SECTIONS}>
      <section id="introduction">
        <h2>Introduction</h2>
        <ul>
          <li>
            This Privacy Policy explains how NursingCareBooking.com ("we", "us", "our") collects,
            uses, stores, and protects your personal information when you use our platform.
          </li>
          <li>
            This policy applies to all users of our platform, including service users (patients and
            their families), care providers, and visitors to our website.
          </li>
          <li>
            We are committed to protecting your privacy and handling your data in accordance with the
            UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </li>
        </ul>
      </section>

      <section id="who-we-are">
        <h2>Who We Are</h2>
        <ul>
          <li>
            NursingCareBooking.com is a care provider marketplace platform that connects families and
            individuals seeking care services with qualified care providers across the United Kingdom.
          </li>
          <li>
            For the purposes of data protection legislation, NursingCareBooking.com is the data
            controller.
          </li>
          <li>
            <strong>Contact:</strong>{' '}
            <a href="mailto:Info@nursingcarebooking.com">Info@nursingcarebooking.com</a>,{' '}
            <a href="tel:+447462365503">+44 7462 365503</a>
            {' | '}
            <a href="https://wa.me/447462365503" target="_blank" rel="noopener noreferrer">WhatsApp</a>
          </li>
          <li>CQC Partner Platform</li>
        </ul>
      </section>

      <section id="information-we-collect">
        <h2>Information We Collect</h2>

        <h3>Account Information</h3>
        <p>
          Full name, email address, telephone number, encrypted password, account type (service user
          or care provider).
        </p>

        <h3>Service User Profile Information</h3>
        <p>
          Residential address (street, city, postal code), house type (apartment, house, bungalow,
          etc.), floor number, lift availability, whether pets are present, relationship to care
          recipient (self, parent, spouse, etc.).
        </p>

        <h3>Care Recipient Information</h3>
        <p>
          Recipient name, age, gender, height, weight, mobility status (independent, needs support,
          walker, wheelchair, bedridden), medical conditions, allergies, current medications, whether
          medical equipment is required, equipment details, mental health condition (dementia,
          Alzheimer's, depression, anxiety, etc.), special care instructions.
        </p>

        <h3>Emergency Contact Information</h3>
        <p>Contact name, telephone number, relationship to care recipient.</p>

        <h3>Care Preferences</h3>
        <p>
          Preferred types of care (domiciliary care, respite care, dementia care, palliative care,
          live-in care, etc.), care shift preference (day, night, full-time, part-time), care
          frequency (daily, weekly, monthly, as-needed), preferred caregiver gender, preferred
          languages.
        </p>

        <h3>Care Provider Profile Information</h3>
        <p>
          Business address, postal code, city, geographic coordinates (latitude/longitude), service
          radius, types of care offered, services provided (elder care, home nursing, personal care,
          meal preparation, medication management, etc.), professional credentials (nursing
          certificate, CPR certified, first aid, CQC registered, NVQ, DBS checked), pricing
          information (hourly, daily, monthly rates), weekly availability schedule, profile
          photographs and gallery images.
        </p>

        <h3>Booking and Transaction Data</h3>
        <p>
          Booking details including appointment dates, service types, notes, booking status, and a
          snapshot of care recipient information captured at the time of booking.
        </p>

        <h3>Communications Data</h3>
        <p>
          Messages exchanged between service users and care providers through our in-app messaging
          system.
        </p>

        <h3>Reviews and Ratings</h3>
        <p>Ratings, written reviews, and moderation status.</p>

        <h3>Location Data</h3>
        <p>
          Addresses provided during registration, and geographic coordinates collected via your
          browser's geolocation API (with your permission) to help match you with nearby care
          providers.
        </p>

        <h3>Technical Data</h3>
        <p>
          Browser type, device information, IP address, pages visited, and interaction data collected
          through analytics services.
        </p>
      </section>

      <section id="special-category-data">
        <h2>Special Category Data (Health Data)</h2>
        <p>
          Our platform collects sensitive personal data classified as "special category data" under
          Article 9 of the UK GDPR, including:
        </p>
        <ul>
          <li>Medical conditions and diagnoses</li>
          <li>Mental health conditions</li>
          <li>Medication information</li>
          <li>Allergies</li>
          <li>Mobility and physical health status</li>
          <li>Equipment and care requirements</li>
        </ul>
        <p>
          We process this data on the basis of your explicit consent, which you provide when
          completing your care profile and submitting booking requests.
        </p>
        <p>
          This health data is essential for matching you with appropriate care providers and ensuring
          the safety and quality of care delivered.
        </p>
        <p>
          You may withdraw your consent at any time by contacting us or deleting your account,
          though this may affect our ability to provide our services to you.
        </p>
      </section>

      <section id="how-we-use-information">
        <h2>How We Use Your Information</h2>
        <ul>
          <li>Providing and operating our care matching platform</li>
          <li>Creating and managing your account</li>
          <li>
            Matching service users with suitable care providers based on location, care needs, and
            preferences
          </li>
          <li>Facilitating bookings between service users and care providers</li>
          <li>Enabling secure in-app communication between users</li>
          <li>Processing and displaying reviews and ratings</li>
          <li>Verifying care provider credentials and CQC registration status</li>
          <li>Moderating content to ensure platform safety and quality</li>
          <li>Sending notifications about bookings, messages, and account activity</li>
          <li>Responding to your enquiries and support requests</li>
          <li>Improving our platform and developing new features</li>
          <li>Complying with legal obligations and regulatory requirements</li>
          <li>Preventing fraud and ensuring platform security</li>
        </ul>
      </section>

      <section id="legal-basis">
        <h2>Legal Basis for Processing</h2>
        <p>
          We process your personal data on the following legal bases under Article 6 of the UK GDPR:
        </p>
        <ul>
          <li>
            <strong>Consent (Article 6(1)(a)):</strong> For processing special category health data,
            location data collected via browser geolocation, and non-essential cookies and analytics.
          </li>
          <li>
            <strong>Contract Performance (Article 6(1)(b)):</strong> For processing data necessary
            to provide our platform services, manage your account, facilitate bookings, and enable
            communications between users.
          </li>
          <li>
            <strong>Legitimate Interests (Article 6(1)(f)):</strong> For improving our platform,
            preventing fraud, ensuring security, and conducting analytics to enhance user experience.
            We balance these interests against your rights and freedoms.
          </li>
          <li>
            <strong>Legal Obligation (Article 6(1)(c)):</strong> For complying with applicable laws,
            regulations, and legal processes, including healthcare regulations and data protection
            requirements.
          </li>
        </ul>
      </section>

      <section id="data-sharing">
        <h2>Data Sharing</h2>
        <p>We share your information with the following parties:</p>
        <ul>
          <li>
            <strong>Between Users:</strong> When a booking is created, relevant care recipient
            information is shared with the assigned care provider. Provider profiles (name,
            qualifications, location, services, ratings) are visible to service users.
          </li>
          <li>
            <strong>Analytics Services:</strong> We use Google Analytics (Tracking ID:
            G-KGSSCQNNEC) and Meta/Facebook Pixel (Pixel ID: 1467140155014290) to analyse platform
            usage. These services collect anonymised usage data. See our Cookie Policy for details.
          </li>
          <li>
            <strong>Infrastructure Providers:</strong> Our platform is hosted on Supabase
            infrastructure. Data is stored securely on their servers.
          </li>
          <li>
            <strong>Regulatory Bodies:</strong> We may share information with the Care Quality
            Commission (CQC) in relation to provider verification and regulatory compliance.
          </li>
          <li>
            <strong>Legal Requirements:</strong> We may disclose your information where required by
            law, court order, or regulatory authority.
          </li>
        </ul>
        <p>We do NOT sell your personal data to third parties.</p>
      </section>

      <section id="data-retention">
        <h2>Data Retention</h2>
        <ul>
          <li>
            <strong>Active Accounts:</strong> We retain your data for as long as your account
            remains active.
          </li>
          <li>
            <strong>Account Deletion:</strong> When you request account deletion, your request is
            reviewed by our team. Upon approval: all bookings are cancelled, care preferences are
            removed, provider relationships are terminated, and your account data is deleted. For
            care providers, CQC verification status and business data are also removed.
          </li>
          <li>
            <strong>Booking Records:</strong> Booking data including patient snapshots may be
            retained for a reasonable period following completion for regulatory compliance and
            dispute resolution purposes.
          </li>
          <li>
            <strong>Reviews:</strong> Published reviews may be retained even after account deletion,
            in anonymised form.
          </li>
          <li>
            <strong>Legal Requirements:</strong> We may retain certain data for longer periods where
            required by law or for the establishment, exercise, or defence of legal claims.
          </li>
        </ul>
      </section>

      <section id="your-rights">
        <h2>Your Rights Under UK GDPR</h2>
        <p>Under UK data protection law, you have the following rights:</p>
        <ul>
          <li>
            <strong>Right of Access:</strong> Request a copy of the personal data we hold about you.
          </li>
          <li>
            <strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete
            data. You can update most information directly through your account settings.
          </li>
          <li>
            <strong>Right to Erasure:</strong> Request deletion of your personal data. You can
            initiate this through the account deletion feature in Settings.
          </li>
          <li>
            <strong>Right to Restrict Processing:</strong> Request that we limit how we use your
            data in certain circumstances.
          </li>
          <li>
            <strong>Right to Data Portability:</strong> Request your data in a structured, commonly
            used, machine-readable format.
          </li>
          <li>
            <strong>Right to Object:</strong> Object to processing based on legitimate interests,
            including profiling and direct marketing.
          </li>
          <li>
            <strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where
            processing is based on consent, without affecting the lawfulness of prior processing.
          </li>
          <li>
            <strong>Right to Lodge a Complaint:</strong> Contact the Information Commissioner's
            Office (ICO) at{' '}
            <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
              ico.org.uk
            </a>{' '}
            or call 0303 123 1113.
          </li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:Info@nursingcarebooking.com">Info@nursingcarebooking.com</a>.
        </p>
      </section>

      <section id="childrens-privacy">
        <h2>Children's Privacy</h2>
        <ul>
          <li>Our platform is not intended for use by individuals under the age of 18.</li>
          <li>
            You must be at least 18 years old to create an account on NursingCareBooking.com.
          </li>
          <li>
            We do not knowingly collect personal data from children under 18. If we discover that we
            have collected data from a child under 18, we will take steps to delete it promptly.
          </li>
          <li>
            The care recipients described in user profiles may be of any age, but the account holder
            managing the booking must be 18 or older.
          </li>
        </ul>
      </section>

      <section id="cookies">
        <h2>Cookies and Tracking Technologies</h2>
        <ul>
          <li>We use cookies and similar technologies to operate our platform and analyse usage.</li>
          <li>Essential cookies are required for authentication and core platform functionality.</li>
          <li>
            Analytics cookies (Google Analytics) help us understand how users interact with our
            platform.
          </li>
          <li>
            Marketing cookies (Meta/Facebook Pixel) help us measure the effectiveness of our
            communications.
          </li>
          <li>
            You can manage your cookie preferences through our cookie consent banner or your browser
            settings.
          </li>
          <li>
            For full details, please see our{' '}
            <Link to={ROUTES.COOKIE_POLICY}>Cookie Policy</Link>.
          </li>
        </ul>
      </section>

      <section id="data-security">
        <h2>Data Security</h2>
        <ul>
          <li>
            We implement appropriate technical and organisational measures to protect your personal
            data.
          </li>
          <li>Passwords are encrypted and never stored in plain text.</li>
          <li>Authentication uses secure bearer token mechanisms.</li>
          <li>Access to personal data is restricted to authorised personnel only.</li>
          <li>We regularly review and update our security practices.</li>
          <li>
            While we take all reasonable precautions, no method of transmission over the internet is
            completely secure. We cannot guarantee absolute security of your data.
          </li>
        </ul>
      </section>

      <section id="international-transfers">
        <h2>International Data Transfers</h2>
        <ul>
          <li>Our primary infrastructure is based in the European Economic Area.</li>
          <li>
            Where data is transferred outside the UK or EEA, we ensure appropriate safeguards are in
            place, including Standard Contractual Clauses approved by the UK Information
            Commissioner's Office.
          </li>
          <li>
            Analytics services (Google, Meta) may process data in jurisdictions outside the UK.
            These transfers are covered by the respective companies' data protection frameworks.
          </li>
        </ul>
      </section>

      <section id="changes">
        <h2>Changes to This Policy</h2>
        <ul>
          <li>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            legal requirements, or platform features.
          </li>
          <li>
            Material changes will be communicated through a notice on our platform or via email.
          </li>
          <li>We encourage you to review this policy periodically.</li>
          <li>
            Your continued use of our platform following any changes constitutes acceptance of the
            updated policy.
          </li>
        </ul>
      </section>

      <section id="contact">
        <h2>Contact Us</h2>
        <ContactBlock
          introText="If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, contact us:"
          extraContent={
            <p>
              You may also contact the Information Commissioner's Office (ICO) if you have concerns
              about how we handle your data:{' '}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
                ico.org.uk
              </a>
              , telephone 0303 123 1113.
            </p>
          }
        />
      </section>
    </PolicyLayout>
  );
};
